<template>
  <v-content class="client billing page">
    <v-progress-linear
      v-if="loading"
      :color="themes.client.light.input"
      indeterminate
      class="my-0"
    />
    <v-container v-else-if="paymentConfig">
      <v-layout column>
        <v-toolbar flat color="transparent" class="mb-4">
          <v-toolbar-title>Billing</v-toolbar-title>
        </v-toolbar>

        <!-- Active subscription -->
        <v-card v-if="subscription" class="mb-4">
          <v-card-title>
            <v-icon left color="green">mdi-check-circle</v-icon>
            <span class="title">Active Subscription</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs6>
                <div class="caption grey--text">Amount</div>
                <div class="title">{{ subscription.currency || paymentConfig.currency }} {{ subscription.amount || paymentConfig.recurringAmount }}</div>
              </v-flex>
              <v-flex xs6>
                <div class="caption grey--text">Interval</div>
                <div class="title text-capitalize">{{ subscription.interval || paymentConfig.recurringInterval }}</div>
              </v-flex>
              <v-flex xs6 class="mt-3">
                <div class="caption grey--text">Status</div>
                <v-chip small :color="subscription.status === 'active' ? 'green white--text' : 'orange white--text'">
                  {{ subscription.status }}
                </v-chip>
              </v-flex>
              <v-flex xs6 class="mt-3" v-if="subscription.registrationId">
                <div class="caption grey--text">Card</div>
                <div class="body-1">**** {{ subscription.lastFour || '****' }}</div>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <!-- Setup new subscription -->
        <v-card v-if="!subscription && !redirecting">
          <v-card-title>
            <span class="title">Setup Recurring Payment</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <p class="body-1">
              Set up a recurring {{ paymentConfig.recurringInterval }} payment of
              <strong>{{ paymentConfig.currency }} {{ paymentConfig.recurringAmount }}</strong>
              for {{ paymentConfig.billingLabel }}.
            </p>
            <p class="body-1 grey--text">
              Your card details will be securely processed by Peach Payments and tokenised for future recurring billing.
              You can cancel at any time by contacting support.
            </p>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn
              :color="themes.client.light.primaryActionButton"
              :loading="initiating"
              @click="startCheckout"
              large
            >
              <v-icon left>mdi-credit-card</v-icon>
              Setup Payment
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card v-if="redirecting || checkoutId" class="mt-4">
          <v-card-title>
            <span class="title">Redirecting to Secure Checkout</span>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-progress-linear
              indeterminate
              :color="themes.client.light.input"
              class="mb-3"
            />
            <p class="body-1 mb-2">
              You are being redirected to Peach Payments to securely save a card for recurring billing.
            </p>
            <p class="body-1 grey--text mb-0">
              If nothing happens, use the setup button again.
            </p>
          </v-card-text>
        </v-card>

        <!-- Error -->
        <v-alert v-if="activeError" :value="true" type="error" class="mt-4">
          {{ activeError }}
        </v-alert>
      </v-layout>
    </v-container>
    <v-container v-else>
      <v-alert :value="true" type="error" class="mt-4">
        Billing is currently unavailable.
      </v-alert>
    </v-container>
  </v-content>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      loading: true,
      initiating: false,
      redirecting: false,
      checkoutError: null,
    }
  },

  created() {
    this.initialize()
  },

  computed: {
    ...mapGetters({
      organization: 'client/organization/model',
      isWhitelisted: 'client/payments/isWhitelisted',
      paymentConfig: 'client/payments/config',
      checkoutId: 'client/payments/checkoutId',
      subscription: 'client/payments/subscription',
      error: 'client/payments/error',
    }),
    activeError() {
      return this.checkoutError || this.error
    },
  },

  methods: {
    async initialize() {
      this.loading = true
      this.redirecting = false
      this.checkoutError = null

      try {
        await this.$store.dispatch('client/organization/fetch')

        if (!this.isWhitelisted) {
          this.$router.replace('/client/applications')
          return
        }

        if (this.$route.query.checkout === 'cancelled') {
          this.$store.dispatch('snackbarError', 'Payment setup was cancelled or did not complete.')
          this.$router.replace('/client/billing')
        }

        if (this.$route.query.checkout === 'complete' && this.$route.query.checkoutId) {
          await this.confirmCheckout({
            checkoutId: this.$route.query.checkoutId,
          })
          return
        }

        await this.$store.dispatch('client/payments/fetchSubscription')
      } catch (e) {
        this.$store.dispatch('snackbarError', 'Unable to load billing right now.')
      } finally {
        this.loading = false
      }
    },

    async startCheckout() {
      this.initiating = true
      this.checkoutError = null

      try {
        const checkout = await this.$store.dispatch('client/payments/initiateCheckout')

        if (!checkout || !checkout.redirectUrl) {
          throw new Error('Missing redirect URL')
        }

        this.redirecting = true
        window.location.assign(checkout.redirectUrl)
      } catch (e) {
        this.redirecting = false
        this.checkoutError = 'Unable to start secure checkout. Please try again.'
      } finally {
        this.initiating = false
      }
    },

    async confirmCheckout(payload) {
      this.checkoutError = null

      try {
        await this.$store.dispatch('client/payments/confirmPayment', payload)
        this.$store.dispatch('snackbarSuccess', 'Payment setup successful!')
      } catch (e) {
        this.$store.dispatch('snackbarError', 'Payment confirmation failed. Please try again.')
      } finally {
        this.$router.replace('/client/billing')
      }
    },
  },

  beforeDestroy() {
    this.$store.dispatch('client/payments/clearCheckout')
  },
}
</script>
